import {
    Component,
    OnInit,
    ViewChild,
    Output,
    EventEmitter,
    Inject,
} from '@angular/core';
import { MatButton } from '@angular/material/button';
import {
    Validators,
    FormGroup,
    FormBuilder,
    ValidationErrors,
    ValidatorFn,
    AbstractControl,
} from '@angular/forms';
import { AppLoaderService } from 'app/shared/services/app-loader/app-loader.service';
import { ErrorService } from 'app/shared/services/error.service';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { StudentService } from '../../services/student.service';
import { DateOfBirthValidator } from '../shared/date-of-birth-validator';

@Component({
    selector: 'app-student-edit',
    templateUrl: './student-edit.component.html',
    styleUrls: ['./student-edit.component.scss'],
})
export class StudentEditComponent implements OnInit {
    @Output() toggleShowStudentUpdateOutput = new EventEmitter<boolean>();
    @ViewChild(MatButton) submitButton: MatButton;

    studentUpdateForm: FormGroup;
    matchedToQCAAStudent = false;
    maxDate: Date;
    showRegisterProfileInvalid = false;
    studentUpdateErrorMessage: string;

    error_messages = {
        firstName: [{ type: 'required', message: 'Please enter a first name' }],
        lastName: [{ type: 'required', message: 'Please enter a last name' }],
        dob: [
            { type: 'required', message: 'Please enter a valid date of birth' },
            { type: 'valid', message: 'A valid age must be between ' + DateOfBirthValidator.MIN_AGE + ' and ' + DateOfBirthValidator.MAX_AGE + ' years.' }
        ],
        phone: [
            { type: 'valid', message: 'Please enter a valid phone number' },
        ],
        lui: [
            { type: 'required', message: 'Please enter a LUI' },
            { type: 'minlength', message: 'LUI must be 10 digits' },
            { type: 'pattern', message: 'LUI must be numeric' },
        ],
    };

    constructor(
        private formBuilder: FormBuilder,
        private studentService: StudentService,
        private loader: AppLoaderService,
        private errorService: ErrorService,
        private _snackBar: MatSnackBar,
        public dialogRef: MatDialogRef<StudentEditComponent>,
        @Inject(MAT_DIALOG_DATA) public data
    ) {}

    ngOnInit() {
        this.matchedToQCAAStudent = this.data.matchedToQCAAStudent;
        const dateNow = new Date();
        this.maxDate = new Date(dateNow);

        this.studentUpdateForm = this.formBuilder.group({
            firstName: [
                this.data.student.personalDetails.firstName,
                Validators.required,
            ],
            lastName: [
                this.data.student.personalDetails.lastName,
                Validators.required,
            ],
            dob: [
                this.data.student.personalDetails.dateOfBirth,
                Validators.compose([
                    Validators.required,
                    DateOfBirthValidator.validate(
                        { valid: true }
                    ),
                ]),
            ],
            phone: [
                this.data.student.personalDetails.phone.replace(/^\+61 /g, ''),
                Validators.compose([
                    this.patternValidator(
                        /^(?:\((?=.*\)))?(0?[2-57-8])\)? ?(\d\d(?:[- ](?=\d{3})|(?!\d\d[- ]?\d[- ]))\d\d[- ]?\d[- ]?\d{3})$/,
                        { valid: true }
                    ),
                ]),
            ],
            lui: [this.data.student.personalDetails.lui, Validators.required],
        });
    }

    onNoClick(): void {
        this.dialogRef.close();
    }

    updateNumber() {
        this.studentUpdateForm.value.phone = this.studentUpdateForm.value.phone.replace(
            /^0+/,
            ''
        );
        this.studentUpdateForm.controls['phone'].setValue(
            this.studentUpdateForm.value.phone
        );
    }

    patternValidator(regex: RegExp, error: ValidationErrors): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } => {
            if (!control.value) {
                // if control is empty return no error
                return null;
            }

            // test the value of the control against the regexp supplied
            const valid = regex.test(control.value);

            // if true, return no error (no error), else return error passed in the second parameter
            return valid ? null : error;
        };
    }

    get formControls() {
        return this.studentUpdateForm.controls;
    }

    async onSubmit() {
        if (!this.studentUpdateForm.invalid) {
            this.showRegisterProfileInvalid = false;
            const studentUpdateData = this.studentUpdateForm.value;
            await window.scrollTo(0, 0);
            try {
                if (studentUpdateData.dob && typeof studentUpdateData.dob == 'object') {
                    studentUpdateData.dob = studentUpdateData.dob.format('YYYY-MM-DD');
                }
                if (studentUpdateData.phone) {
                    studentUpdateData.phone = '+61 ' + studentUpdateData.phone;
                }
                this.loader.open('Please wait');
                await this.studentService.studentUpdate(studentUpdateData);
                this.studentService.clearCache('student');
                this.loader.close();
                this.toggleShowStudentUpdateOutput.emit(false);
                this.dialogRef.close();
                this._snackBar.open(
                    'Details successfully updated!',
                    'Close',
                    {
                        duration: 8000,
                        panelClass: ['success-snackbar'],
                    }
                );
                location.reload();
            } catch (err) {
                this.loader.close();
                this.showRegisterProfileInvalid = true;
                this.studentUpdateErrorMessage = this.errorService.mapError(
                    JSON.parse(err.response).message
                );
            }
        }
    }
}
